import { createInfiniteQuery } from '@funtech-int/rival';
import { Showcase, showcaseQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useInfinitePagination, useResources } from '@funtech-int/rival-react';
import { useFeatureFlags, useAuthManager } from '@funtech-int/yango-shared';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { clientConfig } from '@/config/client';
import { EmptyShowcaseError } from '@/shared/lib/errors/EmptyShowcaseError';

import { InMemoryCache } from '../lib/inMemoryCache';
import { getMovieUGCKey } from '../lib/movieUGC';

import { useGraphqlClient } from './useGraphqlClient';
import { useInMemoryCache } from './useInMemoryCache';

const SHOWCASE_LIMIT = 10;

const { isStyledShowcaseEnabled } = clientConfig;

interface ShowcaseParams {
    id: string;
    withUserData: boolean;
    billingFeaturesCount: number;
    withRating: boolean;
    withStyledShowcasesSelection: boolean;
}

interface ShowcaseQueryContext {
    params: ShowcaseParams;
    client: GraphqlClient;
    inMemoryCache: InMemoryCache;
}

interface ShowcasePageInfo {
    offset: number;
}

const showcaseResourceQuery = createInfiniteQuery<ShowcaseQueryContext, Showcase, ShowcasePageInfo>(
    {
        key(ctx) {
            return [
                showcaseQuery.name,
                ctx.params.id,
                ctx.params.withUserData,
                ctx.params.withRating,
                ctx.params.withStyledShowcasesSelection,
                ctx.params.billingFeaturesCount,
            ].join(':');
        },
        fetch(ctx, prev, pageInfo) {
            const offset = pageInfo?.offset ?? 0;

            return ctx.client
                .fetch({
                    operation: showcaseQuery,
                    variables: {
                        id: ctx.params.id,
                        withUserData: ctx.params.withUserData,
                        offset,
                        limit: SHOWCASE_LIMIT,
                        selectionOffset: 0,
                        selectionLimit: 20,
                        withRating: ctx.params.withRating,
                        withStyledShowcasesSelection: ctx.params.withStyledShowcasesSelection,
                    },
                })
                .then(({ data }) => {
                    if (!data || !data.selections.length) {
                        throw new EmptyShowcaseError(ctx.params.id);
                    }

                    data.selections.forEach((selection) => {
                        if (selection.type === 'default') {
                            selection.items.forEach((item) => {
                                ctx.inMemoryCache.set(
                                    getMovieUGCKey(item.id, 'plannedToWatch'),
                                    Boolean(item.userData?.isPlannedToWatch)
                                );
                            });
                        }
                    });

                    const prevSelections = prev?.selections ?? [];

                    return {
                        ...data,
                        selections: prevSelections.concat(data.selections),
                    };
                });
        },
        nextPageInfo(data) {
            if (data?.hasMore) {
                return {
                    offset: data.offset + SHOWCASE_LIMIT,
                };
            }
        },
        toJSON(data) {
            return data;
        },
        hydrate(data: any) {
            return showcaseQuery.hydrate(data) as any;
        },
    }
);

interface UseShowcaseQueryParams {
    id: string;
}

export function useShowcaseQuery({ id }: UseShowcaseQueryParams) {
    const { flags } = useFeatureFlags();
    const client = useGraphqlClient();
    const inMemoryCache = useInMemoryCache();
    const { isAuthorized } = useAuthManager();
    const pagination = useInfinitePagination();
    const billingFeaturesCount = useProfileManager().billingFeatureNames.get().length;

    const showcaseRefetchTime = flags.get('showcaseRefetchTime', 'number', 3600);

    const [showcase] = useResources(
        showcaseResourceQuery(
            {
                client,
                inMemoryCache,
                params: {
                    id,
                    withUserData: isAuthorized,
                    billingFeaturesCount,
                    withRating: false,
                    withStyledShowcasesSelection: isStyledShowcaseEnabled,
                },
            },
            { pagination, lifetime: showcaseRefetchTime * 1000 }
        )
    );

    return { showcase, pagination };
}
